import { ActionIcon, Box, Flex, Menu, Text } from '@mantine/core';
import { useState } from 'react';
import Button from 'src/components/design-system/buttons/Button';
import { IconDotsVertical } from '@tabler/icons-react';
import { GroupedSuggestions } from 'src/common/components/markdown-editor/plugins/SuggestionTrackerPlugin';
import { ContentHeader } from 'src/UI/app-container';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { DocumentVersion } from 'src/suitability-report/hooks/useDocumentVersions';
import { FinaliseDocumentModal } from './FinaliseDocumentModal';
import MarkersPanel from './MarkersPanel';
import VersionTile from './VersionTile';

type DocumentPanelProps = {
  backHref: string;
  title: string;
  version: string;
  versionDate: string;
  versionVariant: 'primary' | 'past';
  versions: DocumentVersion[];
  selectedVersionId?: string;
  suggestions: GroupedSuggestions;
  showMarkers: boolean;
  isSaving?: boolean;
  onShowMarkersChange: (value: boolean) => void;
  onSave: () => void;
  onSaveVersion: () => void;
  onRemoveVersion: (versionId: string) => void;
  onOverwriteVersion: (versionId: string) => void;
  onSelectVersion: (versionId: string) => void;
  onMakePrimary: (versionId: string) => void;
  onDownloadPdf: (fileId: number) => void;
  onFinalise: () => Promise<void>;
};

export default function DocumentPanel({
  backHref,
  title,
  version,
  versionDate,
  versionVariant,
  versions,
  selectedVersionId,
  suggestions,
  showMarkers,
  isSaving,
  onShowMarkersChange,
  onSave,
  onSaveVersion,
  onRemoveVersion,
  onOverwriteVersion,
  onSelectVersion,
  onMakePrimary,
  onDownloadPdf,
  onFinalise,
}: DocumentPanelProps) {
  const [finaliseModalOpen, setFinaliseModalOpen] = useState(false);

  const primaryVersion = versions.find(v => v.isPrimary);
  const secondaryVersions = versions.filter(v => !v.isPrimary);
  const selectedVersion = versions.find(v => v.id === selectedVersionId);

  return (
    <>
      <ContentHeader
        backHref={backHref}
        left={
          <Text size="sm" fw={FONT_WEIGHTS.bold} c="black">
            {title}
          </Text>
        }
        right={
          <Flex align="center">
            <VersionTile
              version={version}
              date={versionDate}
              variant={versionVariant}
              versions={versions}
              selectedVersionId={selectedVersionId}
              onSaveVersion={onSaveVersion}
              onRemoveVersion={onRemoveVersion}
              onOverwriteVersion={onOverwriteVersion}
              onSelectVersion={onSelectVersion}
              onMakePrimary={onMakePrimary}
              onDownloadPdf={onDownloadPdf}
            />
            <Box w="xs" />
            <Button px="sm" size="sm" variant="secondary" onClick={onSave} loading={isSaving}>
              Save
            </Button>
            <Box w="xs" />
            {versionVariant === 'primary' ? (
              <Button px="sm" size="sm" onClick={() => setFinaliseModalOpen(true)}>
                Finalise
              </Button>
            ) : (
              <Button
                px="sm"
                size="sm"
                onClick={() => selectedVersionId && onMakePrimary(selectedVersionId)}
              >
                Set as primary
              </Button>
            )}
            <Box w="lg" />
            <Menu position="bottom-end" withinPortal>
              <Menu.Target>
                <ActionIcon variant="subtle" c="black">
                  <IconDotsVertical size={20} />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item
                  onClick={() => selectedVersion?.fileId && onDownloadPdf(selectedVersion.fileId)}
                  disabled={!selectedVersion?.fileId}
                >
                  Export as PDF
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Flex>
        }
      />

      {(Object.keys(suggestions.default).length > 0 ||
        Object.keys(suggestions.note).length > 0) && (
        <Box mt="md">
          <MarkersPanel
            suggestions={suggestions}
            showMarkers={showMarkers}
            onShowMarkersChange={onShowMarkersChange}
          />
        </Box>
      )}

      {primaryVersion && (
        <FinaliseDocumentModal
          opened={finaliseModalOpen}
          onClose={() => setFinaliseModalOpen(false)}
          primaryVersion={primaryVersion}
          secondaryVersions={secondaryVersions}
          onConfirm={onFinalise}
        />
      )}
    </>
  );
}
